/**
 * Show details for one Launch queue.
 */

import {Alert} from '@wandb/weave/components/Alert';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router';
import {useHistory} from 'react-router-dom';

import {urlPrefixed} from '../../../config';
import {usePrevious} from '../../../state/hooks';
import {setDocumentTitle} from '../../../util/document';
import {cleanTabName} from '../../../util/string';
import {launchOverview} from '../../../util/urls';
import * as Urls from '../../../util/urls';
import {
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWithLink,
  BannerTextWrapper,
} from '../../Banner';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {DeleteQueueDialog} from '../Queues/DeleteQueueDialog';
import {EnableQueuePrioritizationDialog} from '../Queues/EnableQueuePrioritizationDialog';
import {useQueue} from '../Queues/hooks';
import {Queue} from '../Queues/queueUtil';
import {isLocalResourceType} from '../utils';
import {AddAgentDialog} from './AddAgentDialog';
import * as S from './QueueDetailContent.styles';
import {QueueDetailHeader} from './QueueDetailHeader';
import {QueueDetailTabContent} from './QueueDetailTabContent';
import {QueueDetailTabs} from './QueueDetailTabs';

type QueueDetailContentParams = {
  entityName: string;
  queueId: string;
  tab?: string;
};

export const QueueDetailContent = () => {
  const {
    entityName,
    queueId,
    tab = 'runs',
  } = useParams<QueueDetailContentParams>();
  const history = useHistory();
  const [deletingQueue, setDeletingQueue] = useState<Queue | null>(null);
  const onQueueDelete = (queueToDelete: Queue) => {
    setDeletingQueue(queueToDelete);
  };
  const closeDeleteDialog = () => {
    setDeletingQueue(null);
  };

  const {
    queue,
    error,
    loading,
    refetch: refetchQueue,
  } = useQueue(entityName, queueId);

  const [prioDialogOpen, setPrioDialogOpen] = useState(false);
  const closePrioDialog = () => {
    setPrioDialogOpen(false);
    if (refetchQueue) {
      refetchQueue();
    }
  };

  const pageTitle = queue?.name
    ? `Queue - ${queue.name} - ${cleanTabName(tab)}`
    : 'Queue';
  useEffect(() => {
    setDocumentTitle(pageTitle);
  }, [pageTitle]);

  const onQueueDeleted = async () => {
    setDeletingQueue(null);
    const url = launchOverview();
    history.push(url);
  };

  const onEnablePrioritization = async () => {
    setPrioDialogOpen(true);
  };

  const onRefresh = () => {
    if (refetchQueue) {
      refetchQueue();
    }
  };

  const [isAddAgentOpen, setIsAddAgentOpen] = useState(false);
  const onAddAgent = () => {
    setIsAddAgentOpen(true);
  };
  const onCloseAddAgent = async () => {
    setIsAddAgentOpen(false);
    onRefresh();
  };

  const queueStatus = queue?.status;
  const previousQueueStatus = usePrevious(queueStatus);
  if (
    previousQueueStatus === 'inactive' &&
    queueStatus === 'active' &&
    isAddAgentOpen
  ) {
    // Automatically close add agent dialog if one is run.
    setIsAddAgentOpen(false);
  }

  if (error) {
    return <Alert severity="error">Error fetching queue details</Alert>;
  }
  if (loading || !queue) {
    return <Loader active size="large" name="queue-detail-loader" />;
  }

  return (
    <S.Panel>
      <AddAgentDialog
        entityName={queue.entityName}
        queueName={queue.name}
        open={isAddAgentOpen}
        onClose={onCloseAddAgent}
        queueStatus={queue.status}
      />
      <DeleteQueueDialog
        queue={deletingQueue}
        onClose={closeDeleteDialog}
        onDelete={onQueueDeleted}
      />
      {prioDialogOpen && (
        <EnableQueuePrioritizationDialog
          queue={queue}
          onClose={closePrioDialog}
        />
      )}
      <QueueDetailHeader
        queue={queue}
        onDelete={onQueueDelete}
        onEnablePrioritization={onEnablePrioritization}
      />
      {queue.status === 'inactive' && (
        <S.BannerWithMargin variant="error">
          <BannerFlexWrapper>
            <Icon name="warning" />
            <BannerTextWrapper>
              <BannerBoldText>This queue is not running</BannerBoldText>
              because it doesn't have any active agents.
            </BannerTextWrapper>
          </BannerFlexWrapper>
          {isLocalResourceType(queue.resourceType) && (
            <S.PointerBoldText onClick={() => onAddAgent()}>
              Add an agent
            </S.PointerBoldText>
          )}
        </S.BannerWithMargin>
      )}
      {queue.items.length === 0 &&
        queue.status === 'active' &&
        !isAddAgentOpen && (
          <S.BannerWithMargin variant="info">
            <BannerFlexWrapper>
              <Icon name="info" />
              <BannerTextWithLink>
                Great! Now that you have an agent running, try submitting a job.
                For examples from model training to deployment check out the
                <S.Link
                  href={urlPrefixed(
                    Urls.jobs({entityName: 'wandb', projectName: 'jobs'})
                  )}>
                  wandb/jobs project
                </S.Link>
                .
              </BannerTextWithLink>
            </BannerFlexWrapper>
          </S.BannerWithMargin>
        )}
      <QueueDetailTabs entityName={entityName} queue={queue} tab={tab} />
      <QueueDetailTabContent
        tab={tab}
        queue={queue}
        refetchQueue={onRefresh}
        onAddAgent={onAddAgent}
      />
    </S.Panel>
  );
};
